/*
 * WHAT THE PERSON CLICKED, AS THE SEARCH UNDERSTANDS IT.
 *
 * The chip row on the Simulate panel holds one selection: some states, and
 * whether writes or errors are wanted. This is the pure half of that — the
 * shape, the toggle, and the questions the panel asks before it searches —
 * with no DOM and no wording, so it runs in a Node test exactly as the search
 * does. The panel owns rendering and sentences; this owns meaning.
 *
 * THE SELECTION IS THE FILTER. It is a `ScanFilter` with every field present,
 * handed to `scan` unchanged, so there is no translation step between what was
 * clicked and what is searched, and one rule (`isEmptyFilter`) for "nothing
 * chosen".
 */
import type { ScanFilter } from '../../../sandbox/statechart/scan-trace';
import type { StateCoverage } from './replay-coverage';

export type ActivitySelection = Required<ScanFilter>;

export const EMPTY_SELECTION: ActivitySelection = { states: [], writes: false, errors: false };

/** The same selection with one state added or removed. */
export function toggleSelectedState(s: ActivitySelection, id: string): ActivitySelection {
  return s.states.includes(id) ? { ...s, states: s.states.filter((x) => x !== id) } : { ...s, states: [...s.states, id] };
}

/** The chosen states the coverage check already knows a replay can never reach.
 *  A state the check does not list is not called unreachable. */
export function unreachableSelected(s: ActivitySelection, states: readonly StateCoverage[]): string[] {
  const blocked = new Set(states.filter((st) => !st.reachable).map((st) => st.id));
  return s.states.filter((id) => blocked.has(id));
}

/**
 * True when a search cannot find anything: states were chosen, every one of
 * them is unreachable, and nothing else was asked for. The document already
 * answers that, and a search would only confirm it slowly.
 */
export function onlyUnreachableSelected(s: ActivitySelection, states: readonly StateCoverage[]): boolean {
  if (s.writes || s.errors || s.states.length === 0) return false;
  return unreachableSelected(s, states).length === s.states.length;
}

/** Above this many states a row of chips becomes a wall, and a picker takes over. */
export const CHIP_LIMIT = 12;

export function pickerMode(stateCount: number): 'chips' | 'select' {
  return stateCount > CHIP_LIMIT ? 'select' : 'chips';
}
